/**
 * The yard a building goes up in, and the furniture of its build: the sky crossing from
 * morning to a gold evening as the build takes its day, the fence and the tree, the
 * building's shadow swinging round with the sun, and the build guide's page held up in the
 * corner, turning to each step under the title the plan prints it with.
 *
 * Every film that builds something builds it here, so the channel's builds look like one
 * place; a film hands in how long its day is and what is standing.
 */

import type { View } from "./film.js";
import type { Ink, Pt } from "./ink.js";
import { GROUND, cloud, fence, guideCard, landscape, sun, tree } from "./props.js";
import { project, type Landings, type Projection, type Shape } from "./shed.js";
import type { Mix } from "./sound.js";
import { clamp, cueAt, ease, mix, span } from "./timing.js";

/** Where the horizon is in the yard: the bottom of the fence. */
export const HORIZON = 118;

/** A build's day, in the build scene's seconds. */
export interface Day {
  /** How long the sun takes to cross. */
  length: number;
  /** When the light goes gold, and when it is golden. */
  evening: readonly [number, number];
  /** Where the tree stands. */
  tree: number;
}

/** The yard at `u` seconds into the build: returns where the sun is on its arc, in radians. */
export function yard(ink: Ink, view: View, u: number, day: Day): number {
  landscape(ink, view, HORIZON);
  // The light goes gold as the day the build took comes to an end.
  const evening = span(u, ...day.evening);
  if (evening > 0) {
    // A glow that is strongest at the horizon, in bands, as a wash laid wet.
    for (const [top, alpha] of [
      [-260, 0.06],
      [-120, 0.08],
      [0, 0.1],
    ] as const) {
      ink.wash(
        [
          { x: view.left - 20, y: top },
          { x: view.right + 20, y: top },
          { x: view.right + 20, y: HORIZON },
          { x: view.left - 20, y: HORIZON },
        ],
        ink.palette.accent,
        alpha * evening,
        0,
      );
    }
  }
  // Up from behind the fence on the left, down behind it on the right: the arc ends
  // with the sun half under the fence's top, never in front of the boards.
  const arc = mix(0.89, 0.11, clamp(u / day.length)) * Math.PI;
  sun(ink, Math.cos(arc) * 520, 150 - Math.sin(arc) * 440, u);
  cloud(ink, ((u * 70 + 200) % 1400) - 700, -260, 40);
  cloud(ink, ((u * 50 + 900) % 1400) - 700, -180, 30);
  fence(ink, view, -6, HORIZON);
  tree(ink, day.tree, GROUND - 6, Math.sin(u * 1.4));
  return arc;
}

/** A building's shadow on the grass, `height` feet tall, cast away from the sun at `arc`. */
export function shadow(ink: Ink, proj: Projection, shape: Shape, height: number, arc: number): void {
  if (height <= 0.3) {
    return;
  }
  const at = (x: number, y: number, z: number): Pt => project(proj, x, y, z);
  const { width: w, depth: d } = shape;
  const lean = -Math.cos(arc) * 0.9;
  const corners = [
    at(0, 0, 0),
    at(w, 0, 0),
    at(w, 0, d),
    at(0, 0, d),
    at(0 + lean * height, 0, 0 + 0.5 * height),
    at(w + lean * height, 0, 0 + 0.5 * height),
    at(w + lean * height, 0, d + 0.5 * height),
    at(0 + lean * height, 0, d + 0.5 * height),
  ];
  ink.wash(hull(corners), ink.palette.shadow, 0.75, 0);
}

/** One of the build guide's steps, under the title the engine's guide prints it with. */
export interface Step {
  at: number;
  title: string;
}

/**
 * The guide's page in the top corner: it swings in with the first step, turns over to each
 * step after, and slides away as `leave` goes from 0 to 1.
 */
export function guideCards(ink: Ink, u: number, steps: readonly Step[], leave: number): void {
  const step = cueAt(
    u,
    steps.map((each) => each.at),
  );
  if (step < 0 || leave >= 1) {
    return;
  }
  const flip = span(u, steps[step]!.at, steps[step]!.at + 0.22);
  const shown = flip < 0.5 && step > 0 ? steps[step - 1]! : steps[step]!;
  const first = steps[0]!.at;
  const enter = ease.back(span(u, first, first + 0.35));
  ink.ctx.save();
  ink.ctx.translate(-(1 - enter + leave) * 420, 0);
  guideCard(ink, -436, -312, shown.title, flip);
  ink.ctx.restore();
}

/** The convex hull of a handful of points, for a shadow on the ground. */
function hull(points: Pt[]): Pt[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Pt, a: Pt, b: Pt): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Pt[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper: Pt[] = [];
  for (const point of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

// ---- the build's sound -----------------------------------------------------------------

/**
 * How fast the builder hammers at each spot of a build, in blows a second, and where in a
 * blow the spot starts: a blow lands each time `u * rate + offset` passes a whole number.
 * The picture draws the swing and the star from it, and the score knocks on it.
 */
export type Hammering = Readonly<Record<number, { rate: number; offset: number; pan: number }>>;

/** Every blow of the hammer in a build, in the build scene's seconds, with where it is. */
export function blowsOf(spots: readonly number[], hammering: Hammering): { at: number; pan: number }[] {
  const blows: { at: number; pan: number }[] = [];
  for (const [key, { rate, offset, pan }] of Object.entries(hammering)) {
    const spot = Number(key);
    const from = spots[spot]!;
    const to = spots[spot + 1]!;
    for (let n = Math.ceil(from * rate + offset); (n - offset) / rate < to; n += 1) {
      blows.push({ at: (n - offset) / rate, pan });
    }
  }
  return blows.sort((a, b) => a.at - b.at);
}

/** A build's stages from the walls on, each a start and an end in the build's seconds. */
export interface Raising {
  left: readonly [number, number, number];
  back: readonly [number, number, number];
  right: readonly [number, number, number];
  front: readonly [number, number, number];
  caps: readonly [number, number];
  rafters: readonly [number, number];
  siding: readonly [number, number];
  roofDeck: readonly [number, number];
  doors: readonly [number, number];
}

/**
 * The walls framed and stood, the caps, the rafters, the sheets and the doors, each piece
 * knocking as the picture lands it (`landings`), and a two-note chime (`chime`, in the
 * film's key) as the last wall stands.
 */
export function raisingSound(mix: Mix, at: number, shape: Shape, plan: Raising, pieces: Landings, chime: readonly [number, number]): void {
  const when = (stage: readonly [number, number] | readonly [number, number, number], p: number): number =>
    at + stage[0] + p * (stage[1] - stage[0]);
  for (const name of ["left", "back", "right", "front"] as const) {
    const stage = plan[name];
    pieces.walls[name].forEach((p, i) => mix.tick(when([stage[0], stage[1]], p), { gain: 0.26, pitch: 1 + (i % 3) * 0.06 }));
    mix.whoosh(at + stage[1], stage[2] - stage[1], { from: 200, to: 900, gain: name === "front" ? 0.45 : 0.3 });
    mix.thud(at + stage[2], { gain: name === "front" ? 0.75 : 0.55, weight: 1.2 });
  }
  mix.glock(at + plan.front[2] + 0.05, chime[0], 0.4);
  mix.glock(at + plan.front[2] + 0.13, chime[1], 0.4);

  mix.tick(at + plan.caps[0] + 0.05, { gain: 0.4, pitch: 1.3 });
  mix.tick(at + plan.caps[0] + 0.2, { gain: 0.4, pitch: 1.35 });
  mix.thud(at + plan.rafters[0] + 0.05, { gain: 0.4, weight: 1.1 });
  pieces.rafters.forEach((p) => {
    mix.tick(when(plan.rafters, p), { gain: 0.42, pitch: 0.85 });
    mix.thud(when(plan.rafters, p), { gain: 0.2, weight: 1.6 });
  });
  // The right wall's sheets go on first, then the front's (`drawShed`).
  const rightSheets = Math.ceil(shape.depth / 4);
  pieces.siding.forEach((p, i) => mix.slap(when(plan.siding, p), { gain: 0.72, pan: i < rightSheets ? 0.25 : -0.1 }));
  pieces.roofDeck.forEach((p) => mix.slap(when(plan.roofDeck, p), { gain: 0.75, pan: 0.05 }));
  pieces.doors.forEach((p, i) => {
    mix.thud(when(plan.doors, p), { gain: 0.55, weight: 0.9, pan: -0.1 + i * 0.1 });
    mix.clank(when(plan.doors, p) + 0.02, { gain: 0.08, base: 1800, pan: -0.1 + i * 0.1 });
  });
}
